export interface Equipment {
  id: string
  equipment_type: string
  rating: number | null
  manufacturer: string
  model: string
  status: string
  location: string
  asset_tag?: string
  cpu?: string
  ram?: string
  storage?: string
  gpu?: string
  os?: string
  screen?: string
  estimated_value?: number
  comments?: string
  claimed_by?: string
  claimed_for?: string
}

export interface Request {
  id: string
  student_name?: string
  school: string
  equipment_type: string
  minimum_rating?: number
  needed_for?: string[]
}

export interface MatchResult {
  equipment: Equipment
  matchScore: number
  supportedTasks: string[]
  unsupportedTasks: string[]
}

// Task capability checking based on equipment rating
const getMinimumRatingForTask = (task: string): number => {
  const taskRequirements: Record<string, number> = {
    email: 1,
    web_browsing: 1,
    office_work: 1,
    document_editing: 1,
    video_conferencing: 4,
    presentations: 4,
    coding: 4,
    photo_editing: 6,
    music_production: 6,
    video_editing: 8,
    '3d_modeling': 8,
    gaming: 8,
    creative_work: 8,
    animation: 8,
    machine_learning: 8,
    cad: 8
  }
  return taskRequirements[task] || 1
}

export const isTaskSupported = (equipment: Equipment, task: string): boolean => {
  const rating = equipment.rating || 0
  const minRating = getMinimumRatingForTask(task)
  return rating >= minRating
}

export const formatTaskName = (task: string): string => {
  const taskLabels: Record<string, string> = {
    email: 'Email',
    web_browsing: 'Web Browsing',
    office_work: 'Office Work',
    document_editing: 'Document Editing',
    video_conferencing: 'Video Conferencing',
    presentations: 'Presentations',
    coding: 'Programming/Coding',
    photo_editing: 'Photo Editing',
    video_editing: 'Video Editing',
    '3d_modeling': '3D Modeling',
    gaming: 'Gaming',
    creative_work: 'Creative Work',
    machine_learning: 'Machine Learning',
    cad: 'CAD',
    music_production: 'Music Production',
    animation: 'Animation'
  }
  return taskLabels[task] || task.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

/**
 * Calculate match score for equipment against a request
 * @param equipment The equipment to evaluate
 * @param request The request with needed tasks
 * @returns Match score (0-100) representing percentage of supported tasks
 */
export const calculateMatchScore = (equipment: Equipment, request: Request): number => {
  if (!request.needed_for || request.needed_for.length === 0) {
    // If no tasks specified, just check if rating meets minimum
    const minRating = request.minimum_rating || 1
    return (equipment.rating || 0) >= minRating ? 100 : 0
  }

  const supportedCount = request.needed_for.filter(task =>
    isTaskSupported(equipment, task)
  ).length

  return Math.round((supportedCount / request.needed_for.length) * 100)
}

/**
 * Get match details for equipment against a request
 */
export const getMatchDetails = (equipment: Equipment, request: Request): MatchResult => {
  const neededTasks = request.needed_for || []
  const supportedTasks: string[] = []
  const unsupportedTasks: string[] = []

  neededTasks.forEach(task => {
    if (isTaskSupported(equipment, task)) {
      supportedTasks.push(task)
    } else {
      unsupportedTasks.push(task)
    }
  })

  const matchScore = calculateMatchScore(equipment, request)

  return {
    equipment,
    matchScore,
    supportedTasks,
    unsupportedTasks
  }
}

/**
 * Find the best matching equipment for a request
 * @param equipmentList List of available equipment
 * @param request The request to match against
 * @returns The best matching equipment, or null if none found
 */
export const findBestMatch = (
  equipmentList: Equipment[],
  request: Request
): MatchResult | null => {
  if (!equipmentList || equipmentList.length === 0) return null

  const matches = equipmentList
    .map(eq => getMatchDetails(eq, request))
    .filter(match => match.matchScore > 0)

  if (matches.length === 0) return null

  // Sort by match score (descending), then by rating (ascending for ties)
  matches.sort((a, b) => {
    if (b.matchScore !== a.matchScore) {
      return b.matchScore - a.matchScore
    }
    // If match scores are equal, prefer lower rating (more appropriate allocation)
    return (a.equipment.rating || 0) - (b.equipment.rating || 0)
  })

  return matches[0]
}

/**
 * Get all equipment sorted by match quality
 */
export const sortEquipmentByMatch = (
  equipmentList: Equipment[],
  request: Request
): MatchResult[] => {
  return equipmentList
    .map(eq => getMatchDetails(eq, request))
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore
      }
      return (a.equipment.rating || 0) - (b.equipment.rating || 0)
    })
}

export const useEquipmentMatching = () => {
  return {
    calculateMatchScore,
    getMatchDetails,
    findBestMatch,
    sortEquipmentByMatch,
    isTaskSupported,
    formatTaskName
  }
}
